import React from 'react'

import { Modal, Backdrop, Card, Box, Typography, Button, ButtonProps } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { makeStyles, createStyles } from '@material-ui/styles'

import { ReactComponent as ErrorIcon } from '@masfront/common-b2c/src/modules/ui/components/NotificationModal/assets/error-icon.svg'
import {
  ModalTypes,
  NotificationModalPalette,
} from '@masfront/common-b2c/src/modules/ui/components/NotificationModal/NotificationModalTypes'
import { CustomTheme } from '@masfront/common-b2c/src/modules/ui/theme/utils'

const useStyles = makeStyles((theme: CustomTheme) => {
  return createStyles({
    '@global': {
      '@keyframes spinReverse': {
        from: {
          transform: 'rotate(0deg)',
        },
        to: {
          transform: 'rotate(-360deg)',
        },
      },
    },
    modal: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
    },
    card: {
      borderBottom: ({ borderColor }: { borderColor: NotificationModalPalette }) =>
        `4px solid ${theme?.palette?.[borderColor]?.main}`,
      width: '936px',
    },
    icon: {
      cursor: 'pointer',
      fontSize: '32px',
    },
    iconAlert: {
      '& .MuiSvgIcon-root': {
        marginTop: ({ borderColor }: { borderColor: NotificationModalPalette }) =>
          borderColor === NotificationModalPalette.loading ? theme.spacing(9) : null,
        width: theme.spacing(14),
        height: theme.spacing(14),
        color: ({ borderColor }: { borderColor: NotificationModalPalette }) =>
          theme?.palette?.[borderColor]?.main,
        animation: ({ borderColor }: { borderColor: NotificationModalPalette }) =>
          borderColor === NotificationModalPalette.loading
            ? 'spinReverse .8s linear infinite'
            : null,
      },
    },
  })
})

export interface ModalAction {
  button?: ButtonProps
  id?: string
  name: string
  onClick?: () => void
}

export interface B2CModalProps {
  isOpen: boolean
  type: string
  title?: string
  description?: string
  children?: React.ReactNode
  actions?: ModalAction[]
  onClose?: () => void
  dataHook?: string
}

export function B2CModal({
  isOpen,
  type,
  title,
  description,
  children,
  actions,
  onClose,
  dataHook = '',
}: B2CModalProps) {
  const { imageModal, borderColor } = ModalTypes[type || 'error']
  const { modal, card, icon, iconAlert } = useStyles({ borderColor })
  return (
    <Modal
      open={isOpen}
      className={modal}
      BackdropComponent={Backdrop}
      disableAutoFocus
      disableEnforceFocus
      data-hook={dataHook}
    >
      <Box component={Card} className={card} borderRadius="14px">
        <Box display="flex" justifyContent="flex-end" width="100%">
          <Box mr={8} mt={8}>
            {type !== NotificationModalPalette.loading ? (
              <CloseIcon
                className={icon}
                onClick={() => {
                  /* eslint-disable no-unused-expressions */
                  onClose ? onClose() : null
                }}
                data-hook="close-modal-button"
              />
            ) : null}
          </Box>
        </Box>
        <Box
          mt={5}
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
        >
          <Box mb={6} className={iconAlert}>
            {imageModal || ErrorIcon}
          </Box>
          <Box mb={6}>
            <Typography variant="h2">{title}</Typography>
          </Box>
          {description && (
            <Box>
              <Typography variant="body1" color="textSecondary">
                {description}
              </Typography>
            </Box>
          )}
          {children}
          {actions && (
            <Box my={14} display="flex">
              {actions?.map(({ button, name, id, onClick }, i) => (
                <Box key={name} mr={6}>
                  <Button
                    data-hook={id ? `modal-action-${id}` : `modal-action${i}`}
                    variant={button?.variant || 'contained'}
                    color={button?.color || 'secondary'}
                    size={button?.size || 'medium'}
                    onClick={onClick}
                  >
                    {name}
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  )
}
